// src/modules/gyms/components/GoogleMapsLinkModal.tsx
import { Modal } from "react-bootstrap";
import { FormEvent, ReactNode, useMemo, useState } from "react";
import {
  parseGoogleMapsUrl,
  GoogleMapsParseResult,
} from "../utils/parse-google-maps-url";
import "./google-maps-link-modal.css";

interface GoogleMapsLinkModalProps {
  show: boolean;
  onClose: () => void;
  onApply: (lat: number, long: number) => void;
}

function highlightUrl(url: string, result: GoogleMapsParseResult | null) {
  if (!result?.highlights.length) return url;

  const sorted = [...result.highlights].sort((a, b) => a.start - b.start);
  const chunks: ReactNode[] = [];
  let cursor = 0;

  sorted.forEach((h, i) => {
    if (h.start > cursor) chunks.push(url.slice(cursor, h.start));
    const cls =
      h.kind === "lat"
        ? "bg-success-subtle text-success-emphasis fw-semibold px-1 rounded"
        : h.kind === "long"
          ? "bg-primary-subtle text-primary-emphasis fw-semibold px-1 rounded"
          : "bg-warning-subtle text-warning-emphasis fw-semibold px-1 rounded";
    chunks.push(
      <mark key={i} className={cls} style={{ background: undefined }}>
        {url.slice(h.start, h.end)}
      </mark>,
    );
    cursor = h.end;
  });
  if (cursor < url.length) chunks.push(url.slice(cursor));
  return chunks;
}

export function GoogleMapsLinkModal({
  show,
  onClose,
  onApply,
}: GoogleMapsLinkModalProps) {
  const [link, setLink] = useState("");
  const [parseResult, setParseResult] = useState<GoogleMapsParseResult | null>(
    null,
  );
  const [linkError, setLinkError] = useState("");

  const handleParse = (value: string) => {
    setLink(value);
    setLinkError("");
    setParseResult(value.trim() ? parseGoogleMapsUrl(value) : null);
  };

  const canApply = parseResult?.lat != null && parseResult?.long != null;

  const legend = useMemo(
    () => (
      <div className="d-flex flex-wrap gap-2 small mb-2">
        <span>
          <mark className="bg-success-subtle text-success-emphasis px-1 rounded">
            lat
          </mark>{" "}
          vĩ độ
        </span>
        <span>
          <mark className="bg-primary-subtle text-primary-emphasis px-1 rounded">
            long
          </mark>{" "}
          kinh độ
        </span>
      </div>
    ),
    [],
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canApply) {
      setLinkError(
        "Không tìm thấy tọa độ trong liên kết. Vui lòng dán liên kết Google Maps hợp lệ.",
      );
      return;
    }
    onApply(parseResult!.lat!, parseResult!.long!);
    setLink("");
    setParseResult(null);
    onClose();
  };

  const handleHide = () => {
    setLink("");
    setParseResult(null);
    setLinkError("");
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      size="lg"
      className="gm-link-modal"
      backdropClassName="gm-link-modal-backdrop"
      contentClassName="shadow-lg border-0"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold fs-6">
          Nhập bằng liên kết Google Maps
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <form onSubmit={handleSubmit}>
          <label className="form-label fw-semibold">Liên kết Google Maps</label>
          <textarea
            className={`form-control ${linkError ? "is-invalid" : ""}`}
            rows={3}
            value={link}
            onChange={(e) => handleParse(e.target.value)}
            placeholder="Dán liên kết từ Google Maps, ví dụ: https://www.google.com/maps/place/..."
          />
          {linkError && (
            <div className="invalid-feedback d-block">{linkError}</div>
          )}

          {parseResult && link.trim() && (
            <div className="mt-3 border rounded-3 p-3 bg-light">
              <div className="fw-semibold small mb-2">
                Cách trích xuất tọa độ
              </div>
              <p className="small text-muted mb-2">
                Hệ thống quét liên kết theo thứ tự ưu tiên (chính xác nhất
                trước). Phần khớp được tô màu trong liên kết bên dưới.
              </p>

              <ol className="small mb-3 ps-3">
                {parseResult.steps.map((step) => (
                  <li
                    key={step.id}
                    className={
                      step.matched ? "text-success fw-semibold" : "text-muted"
                    }
                  >
                    <span>{step.label}</span>
                    <span className="text-muted fw-normal">
                      {" "}
                      — mẫu <code>{step.pattern}</code>
                    </span>
                    {step.matched ? (
                      <div className="mt-1">
                        ✓ Khớp: <code>{step.matchSnippet}</code>
                        {step.lat != null && step.long != null && (
                          <span>
                            {" "}
                            → vĩ độ <strong>{step.lat}</strong>, kinh độ{" "}
                            <strong>{step.long}</strong>
                          </span>
                        )}
                        {step.note && (
                          <div className="text-muted">{step.note}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted"> — không khớp</span>
                    )}
                  </li>
                ))}
              </ol>

              {legend}
              <div
                className="small font-monospace border rounded p-2 bg-white"
                style={{ wordBreak: "break-all", lineHeight: 1.6 }}
              >
                {highlightUrl(link, parseResult)}
              </div>

              {canApply && (
                <div className="alert alert-success border-0 mt-3 mb-0 py-2 small">
                  Kết quả: vĩ độ <strong>{parseResult.lat}</strong>, kinh độ{" "}
                  <strong>{parseResult.long}</strong>
                </div>
              )}
              {!canApply && parseResult.steps.every((s) => !s.matched) && (
                <div className="alert alert-warning border-0 mt-3 mb-0 py-2 small">
                  Không tìm thấy tọa độ trong liên kết này.
                </div>
              )}
            </div>
          )}

          <div className="text-end mt-4 pt-2 border-top">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={handleHide}
            >
              Hủy
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canApply}
            >
              Áp dụng tọa độ
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
